import {Component} from '@angular/core';
import {
  DatatableActionButton,
  DatatableEvent,
  DatatableHandler,
  DatatableHandlerEvents,
  DatatableHandlerSettings,
  ErrorHandlingMode,
  IconType,
} from '../datatable/datatable/datatable.component';
import {ActivatedRoute, Router} from '@angular/router';
import {ExamReportsClient, ExamSubjectsClient, StudentsClient} from '../../../generated-client/generated-api-client';
import {StudentExamDataService} from '../../../services/data-services/student-exam-data.service';
import {SimpleModalService} from 'ngx-simple-modal';
import {ToastrService} from 'ngx-toastr';
import {AddExamSubjectModalComponent} from '../../modals/add-exam-subject-modal/add-exam-subject-modal.component';
import {DataTableDataService} from '../../../services/data-services/navigation-data-services';
import {SyncDataService} from '../../../services/data-services/sync-data.service';
import {DataTableRoutes} from 'src/app/constants/datatableUrls';
import {ErrorProcessorService} from '../../../services/error-processor.service';
import {ExamReportDataService} from "../../../services/data-services/exam-report-data.service";
import { ExamDateShiftConfirmModalComponent } from '../../modals/exam-date-shift-confirm-modal/exam-date-shift-confirm-modal.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { Observable } from 'rxjs';
import { PersistentDataService } from '../../../services/persistent-data.service';

@Component({
  selector: 'app-exam-subject-datatable',
  templateUrl: './exam-subject-datatable.component.html',
  styleUrls: ['./exam-subject-datatable.component.sass'],
})
export class ExamSubjectDatatableComponent {
  private _dtActionButtonsFn: (data: any) => DatatableActionButton[] = data => {
    const deleteAction = new DatatableActionButton('exam_subject_delete', 'Vizsgatárgy törlése', 'delete', undefined, IconType.Danger, !this.isModifiable);
    return [deleteAction];
  };

  dtHandler?: DatatableHandler;
  dtSettings: DatatableHandlerSettings = DatatableHandler.getDefaultSettings();
  dtEvents: DatatableHandlerEvents = DatatableHandler.getDefaultEvents();
  dtDataSourceUrl: string = DataTableRoutes.ExamSubjectList;
  studentId?: string;
  isModifiable!: boolean;
  canLoad: boolean = false;
  tableLabel: string = '';
  examReportId: string = '';

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _examSubjectsClient: ExamSubjectsClient,
    private _studentExamDataService: StudentExamDataService,
    private _modalService: SimpleModalService,
    private _dataTableService: DataTableDataService,
    private _toastr: ToastrService,
    private _syncService: SyncDataService,
    private errorProcessorService: ErrorProcessorService,
    private _studentsClient: StudentsClient,
    private _examReportsClient: ExamReportsClient,
    private _examReportDataService: ExamReportDataService,
    private _pDservice: PersistentDataService
  ) {
    this.isModifiable = this._examReportDataService.isModifiable.value!;
    this._route.params.subscribe(next => {
      this.studentId = next['id'];
      this.dtHandler?.refresh();
    });
    this._studentExamDataService.canLoad.subscribe(next => {
      this.canLoad = next;
    });

    this._studentExamDataService.studentName.subscribe(next =>
      this.setTableLabel(next, this._studentExamDataService.registrationNumber.value)
    );

    this._studentExamDataService.registrationNumber.subscribe(next =>
      this.setTableLabel(this._studentExamDataService.studentName.value, next)
    );

    this.dtSettings.pageSize = _pDservice.examSubjectsPageSize;
    this.dtSettings.searchString = _pDservice.examSubjectsSearchString;
    
    this.dtSettings.errorHandlingMode = ErrorHandlingMode.Toastr;
    this.dtSettings.order = [[0, 'asc']];
    this.dtSettings.additionalRequestData = () => {
      if (this.dtHandler?.pageSize != null)
        _pDservice.examSubjectsPageSize = this.dtHandler?.pageSize
      if (this.dtHandler?.searchString != null)
        _pDservice.examSubjectsSearchString = this.dtHandler?.searchString
      return {
        studentId: this.studentId,
      };
    };
    this.dtSettings.actions = this._dtActionButtonsFn;
  }

  get dataTableFromService() {
    return this._dataTableService.dataTable;
  }

  get studentName() {
    return this._studentExamDataService.studentName;
  }

  get registrationNumber() {
    return this._studentExamDataService.registrationNumber;
  }

  onDtInit(dtHandler: DatatableHandler) {
    this.dtHandler = dtHandler;
    this.setArchivedSpeicificDtSettings(this.isModifiable);
    this.dtHandler?.create();
  }

  onAction(event: DatatableEvent) {
    if (event.actionId === 'exam_subject_delete') {
      this.delete(event.data['id']);
    }
  }

  private setArchivedSpeicificDtSettings(isModifiable: boolean): void {
    if (isModifiable) {
      this.dtSettings.buttons = [
        {
          text: 'Vizsgatárgy hozzáadása',
          class: 'btn-primary text-white',
          action: () => {
            if (this.studentId) {
              this._modalService
                .addModal(AddExamSubjectModalComponent, {studentIdParam: this.studentId})
                .subscribe(isConfirmed => {
                  if (isConfirmed) {
                    this.dataTableFromService?.refresh(() => {
                      if (this.studentId) {
                        const row = this.dataTableFromService?.getRowById(this.studentId);
                        row?.classList?.add('selected');
                      }
                    });
                    this.dtHandler?.refresh();
                    this._syncService.afterSuccessfulOperation.next();
                  }
                });
            }
          },
        },
      ];
    } else {
      this.dtSettings.additionalColumns = [
        {
          targets: 7,
          orderable: false,
          visible: false,
        },
      ];
    }
  }

  private setTableLabel(studentName: string, registrationNumber?: string) {
    if (!registrationNumber) this.tableLabel = studentName;
    else this.tableLabel = studentName + ' - ' + registrationNumber;
  }

  private delete(id: string): void {
    var modal = new Observable<boolean>();

    if(this.dtHandler?.rowCount == 1) {
      modal = this._modalService
      .addModal(ConfirmModalComponent,
        {
          title: 'Vizsgázóhoz rendelt vizsgatárgy törlése',
          message: 'Biztosan törli a vizsgatárgyat?',
          confirmText: 'Törlés',
          confirmClass: 'btn-danger text-white',
        });
    } else {
      modal = this._modalService
      .addModal(ExamDateShiftConfirmModalComponent, {
        title: 'Szeretné a törölt vizsgatárgyat követő összes elem kezdési időpontját eltolni?',
        studentId: this.studentId!,
        subjectId: id,
      });
    }
      modal.subscribe(result => {
        if (result!=null) {
          this._examSubjectsClient.delete({examSubjectId: id, updateExamSubjectStartDates: result, studentId: this.studentId}).subscribe(
            () => {
              this.dtHandler?.refresh();
              this._toastr.success('Sikeres törlés');
            },
            error => this.errorProcessorService.processError(error, true, false, undefined, true)
          );
        }
      });
  }
}
